import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { MANIFESTO, IMG } from "@/data/site";
import Seo from "@/components/Seo";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <div className="bg-ink pt-[70px] md:pt-[84px]">
      <Seo title="Über STARSOX" description="Die Marke für kompromisslose Performance. Erfahre mehr über STARSOX – Designed for Performance. Built for Champions." path="/about" />
      {/* Hero */}
      <section ref={ref} className="relative h-[70vh] min-h-[480px] overflow-hidden grain">
        <motion.div style={{ y }} className="absolute inset-0 scale-110">
          <img src={IMG.tunnel} alt="Athlet im Stadiontunnel" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
        </motion.div>
        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-14">
          <p className="overline mb-4">Über STARSOX</p>
          <MaskedLines
            start
            className="font-heading uppercase leading-[0.86]"
            lineClassName="text-6xl md:text-8xl lg:text-9xl"
            lines={["Mehr als", <span key="s" className="text-gold">eine Socke.</span>]}
          />
        </div>
      </section>

      {/* Intro */}
      <section className="container-x py-20 md:py-28">
        <Reveal className="max-w-3xl">
          <p className="text-2xl md:text-3xl leading-snug text-white/85">
            STARSOX steht für kompromisslose Performance. Wir bauen keine Socken von der Stange –
            wir entwickeln ein Premium-Produkt für Spieler, die jeden Vorteil wollen.
          </p>
        </Reveal>
      </section>

      {/* Manifesto chapters */}
      <section className="border-t border-white/10">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.no}>
            <div className={`container-x py-16 md:py-24 grid md:grid-cols-12 gap-8 items-start ${i !== 0 ? "border-t border-white/10" : ""}`}>
              <div className="md:col-span-3">
                <span className="font-heading text-7xl md:text-8xl text-white/10">{m.no}</span>
              </div>
              <div className="md:col-span-9 max-w-2xl">
                <h2 className="font-heading text-3xl md:text-5xl uppercase leading-[0.95]">{m.title}</h2>
                <p className="mt-5 text-white/60 leading-relaxed text-lg">{m.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 py-8 bg-carbon">
        <Marquee>
          {["Designed for Performance", "Built for Champions"].concat(["Designed for Performance", "Built for Champions"]).map((w, i) => (
            <span key={i} className="font-heading text-4xl md:text-6xl uppercase text-outline-dim px-10">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* CTA */}
      <section className="container-x py-20 md:py-28 text-center">
        <Reveal>
          <h2 className="font-heading text-4xl md:text-6xl uppercase">Bereit für mehr Grip?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/product" data-testid="about-shop" className="btn-primary">Jetzt shoppen <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/teams" className="btn-ghost">Für Teams</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
